/**
 * SOVRANOVA — Guide ("How to use SOVRANOVA")
 *
 * User-facing manual: what each LIVE feature is for, how to actually use it,
 * and which one suits which moment (David's request, 19 Aug). Same honesty
 * rules as AboutView: wellness companion framing, no treatment claims, no
 * outcome promises. Every entry has an "Open →" that jumps straight there —
 * a guide that ends in a tap, not a lecture.
 *
 * Flag-gated entries mirror the SideMenu gates so this page never describes
 * a feature the build doesn't ship.
 */
import { useState } from 'react'

const F_INTIMACY   = import.meta.env.VITE_FEATURE_INTIMACY === 'true'
const F_MEDITATION = import.meta.env.VITE_FEATURE_MEDITATION === 'true'
const F_COUPLES    = import.meta.env.VITE_FEATURE_COUPLES === 'true'
const F_VITALITY   = import.meta.env.VITE_FEATURE_VITALITY === 'true'
const F_AMBIENT    = import.meta.env.VITE_FEATURE_AMBIENT === 'true'
// 8-hour Sleep Protocol tone — off for beta wave 1 (4 Sep 2026 audit, D-A).
const F_SLEEP_PROTOCOL = import.meta.env.VITE_FEATURE_SLEEP_PROTOCOL === 'true'
const F_LIVE       = import.meta.env.VITE_FEATURE_LIVE_COMPANION === 'true'
const F_HUME_VOICE = import.meta.env.VITE_FEATURE_HUME_VOICE === 'true'

// Situation → the feature that fits it. This is the piece people actually
// need on a hard day: not a catalogue, a signpost.
const SUITS = [
  { feel: 'I just need to get something off my chest',        go: '🎙 Vent — speak freely, no fixing, just being heard' },
  { feel: 'I want to properly work through something',        go: '💬 AI Session — a structured conversation that remembers you' },
  { feel: "I can't settle / my mind won't stop",              go: '🧘 Yoga Nidra (in Meditation) or 🧠 a Guided Session — lie down and be guided' },
  { feel: "I can't sleep",                                    go: F_SLEEP_PROTOCOL ? '🌙 Sleep — an overnight wind-down protocol with a breathing guide' : '🧠 Guided Sessions → Sleep Session, or 🧘 Meditation → Evening Wind-Down — a spoken wind-down you can fall asleep to' },
  { feel: 'My partner and I need to talk about something hard', go: '💬 AI Session → Couples — a guided space with built-in pauses' },
  { feel: 'I just came out of an appointment or a big conversation', go: '🪞 Reflect — debrief it in your own words while it\'s fresh' },
  { feel: 'I want a lift before the day starts',              go: '✨ Affirmations or your morning check-in on Journey' },
  { feel: 'I want to talk naturally, hands-free',             go: '🎧 Sovranova Live — it hears how you feel, not just your words' },
]

const GROUPS = [
  {
    label: 'Every day',
    items: [
      { emoji: '🧭', name: 'Journey', view: 'dashboard',
        what: 'Your home base. The companion greets you, a daily check-in takes ten seconds, and your streak and wellbeing trends live here.',
        how: 'Open the app and you are on it. Answer the one-tap check-in when it appears; your companion speaks its welcome once per day and then stays quiet.',
        when: 'Start and end of the day, or any time you want the overview.' },
      { emoji: '💬', name: 'AI Session', view: 'session',
        what: 'A structured, supportive conversation that remembers your past sessions. Speak or type. This is where deeper work happens.',
        how: 'Tap Start, talk or type as you like — the mic button records, and the companion answers aloud. "End session" gives you a spoken summary before it closes.',
        when: 'You want to untangle something properly, not just release it.' },
      { emoji: '🎙', name: 'Vent', view: 'vent',
        what: 'Unstructured release. It listens to how you sound, receives what you say, and gives it back gently. No advice unless you ask.',
        how: 'One tap opens the mic, then it is hands-free. Talk as long as you need; leave whenever you are done.',
        when: 'You are full and just need to be heard.' },
      ...(F_LIVE ? [{ emoji: '🎧', name: 'Sovranova Live', view: 'live',
        what: 'A live, hands-free voice conversation. It responds to your tone and feeling as well as your words.',
        how: 'Tap once to open the mic, then just talk — like a phone call.',
        when: 'You want natural back-and-forth without a screen between you.' }] : []),
    ],
  },
  {
    label: 'Rest & practice',
    items: [
      { emoji: '🧠', name: 'Guided Sessions', view: 'hypnosis',
        what: 'Guided relaxation in four styles: voice-only Guided, Frequency-Enhanced (voice with a soft background tone), Sleep Session (a spoken wind-down you can fall asleep to), and Tone & Affirmations (a soft tone with slow on-screen affirmations).',
        how: 'Pick a style, a focus and a length, then Begin. Lie back — the screen dims itself to save battery and stays awake so the session never cuts out. Tap the screen to bring it back.',
        when: 'Wound up, before bed, or to reset in the middle of a hard day.' },
      ...(F_MEDITATION ? [{ emoji: '🧘', name: 'Meditation · Yoga Nidra', view: 'meditation',
        what: 'Mindfulness sessions, and the long-form Yoga Nidra practice — a guided, lying-down rotation of attention with ambient scenes (rain, forest, waterfall).',
        how: 'From the menu choose MEDITATION. For Nidra: pick 20 or 30 minutes, lie down, set your resolve when invited, and let it carry you. Nothing is asked of you except to lie still and listen.',
        when: 'Deep rest, recovery, and the nights sleep feels far away.' }] : []),
      ...(F_SLEEP_PROTOCOL ? [{ emoji: '🌙', name: 'Sleep', view: 'sleep',
        what: 'An overnight tone: a very quiet two-tone sound with a breathing guide for the first few minutes.',
        how: 'Start it as you get into bed and put the phone down face-up nearby. The screen goes almost black on its own and stays that way; the audio runs through the night.',
        when: 'Bedtime.' }] : []),
      { emoji: '✨', name: 'Affirmations', view: 'affirmations',
        what: 'Personalised affirmations by theme — self-worth, calm, confidence, letting go — read to you in your chosen voice.',
        how: 'Pick a theme, tap play, listen or read along.',
        when: 'A steadying start to the day, or before something daunting.' },
    ],
  },
  {
    label: 'Reflect & record',
    items: [
      ...(F_AMBIENT ? [{ emoji: '🪞', name: 'Reflect', view: 'ambient',
        what: 'Debrief anything in your own words — a therapy appointment, a hard conversation, a big day — and receive it back as a structured reflection you can revisit.',
        how: 'Reflect opens on "Your reflections" — your archive. Open one to keep reflecting on it (everything you add stays with that reflection), or tap "Reflect on something new" for something not here yet (your voice is never stored — words only). "Continue this in a session" reopens any reflection as a full session. Also one tap away from AI Session via the Reflections tab.',
        when: 'Right after something that deserves to be kept and understood.' }] : []),
      { emoji: '📖', name: 'Journal', view: 'journal',
        what: 'Private reflective writing. Yours alone.',
        how: 'Open, write, close. That is the whole method.',
        when: 'When words want to be written rather than spoken.' },
      { emoji: '📋', name: 'Summary for a professional', view: 'handover',
        what: 'A clear brief of your recent themes and check-ins to hand to a GP, psychologist or counsellor, so you never start from zero in an appointment.',
        how: 'Open it from the menu, review what it prepared, and share it if and only if you choose to.',
        when: 'Before an appointment.' },
    ],
  },
  ...((F_COUPLES) ? [{
    label: 'For your relationship',
    items: [
      { emoji: '💞', name: 'Couples (in AI Session)', view: 'session',
        what: 'A guided space for the two of you, together on one phone. The companion facilitates: everyone gets the floor, hard questions get softened, and if one of you starts shutting down it calls a short pause with a way back — the pauses are the design, not a malfunction.',
        how: 'Open AI Session, switch to Couples, enter both first names (voices are never recorded — transcript only). Tap whoever is speaking; tap your own name when done, or your partner\'s name to pass to them. At the end, "Complete & save to Reflections" keeps the conversation for you both.',
        when: 'The conversations that keep getting postponed.' },
      { emoji: '💞', name: 'Couples practices', view: 'couples',
        what: 'Structured relationship exercises to do together, outside of a facilitated conversation.',
        how: 'Open COUPLES from the menu and pick a practice.',
        when: 'Building the habit in calm weather, not just the storms.' },
    ],
  }] : []),
  {
    label: 'Focused journeys',
    items: [
      // Hume-only journeys — contained for beta wave 1 (H1); listed only when enabled.
      ...(F_HUME_VOICE ? [
      { emoji: '🛡️', name: 'MASCULINE', view: 'masculine',
        what: "A strength-based voice companion for men — performance, fatherhood, work and identity, and the things that are hard to say out loud.",
        how: 'Pick the pathway that fits, tap to talk.',
        when: 'You want a coach in your corner, not a couch.' },
      { emoji: '🌷', name: 'FEMININE', view: 'feminine',
        what: 'A grounded companion for women — motherhood and identity, the mental load, people-pleasing and the lost self.',
        how: 'Pick the pathway that fits, tap to talk.',
        when: 'Holding everything for everyone and needing somewhere to put it down.' },
      { emoji: '🌊', name: 'SOVEREIGN FLOW', view: 'sovereign_flow',
        what: 'The feelings underneath money — fear, shame, old stories about worth. Emotional support only; never financial advice.',
        how: 'Pick a pathway, tap to talk.',
        when: 'Money is keeping you up at night.' },
      ] : []),
      { emoji: '⚓', name: 'ANCHOR', view: 'anchor',
        what: 'Steadiness and pacing — for when everything is moving too fast.',
        how: 'Open it and follow the practice it offers.',
        when: 'Overwhelm, urgency, the racing feeling.' },
      ...(F_VITALITY ? [{ emoji: '🏃', name: 'VITALITY', view: 'vitality',
        what: 'Movement and fitness support with calm pacing.',
        how: 'Open it and choose a movement session.',
        when: 'The body needs to move for the mind to settle.' }] : []),
      ...(F_INTIMACY ? [{ emoji: '🌙', name: 'INTIMACY', view: 'intimacy',
        what: 'Connection and intimate wellbeing, handled with care. Adults only.',
        how: 'Open it from the menu and choose where to begin.',
        when: 'Closeness deserves the same care as everything else.' }] : []),
    ],
  },
]

export function GuideView({ onClose, onNavigate }) {
  const [open, setOpen] = useState(null)   // `${groupIdx}-${itemIdx}`

  return (
    <div style={s.page}>
      <button style={s.back} onClick={onClose}>← Back</button>

      <div style={s.hero}>
        <p style={s.kicker}>Guide</p>
        <h1 style={s.h1}>How to use SOVRANOVA</h1>
        <p style={s.lede}>Every space in here, what it is for, and how to use it. Tap any feature to see how it works — and open it from right here.</p>
      </div>

      <section>
        <h3 style={s.sectionTitle}>What suits you right now?</h3>
        <div style={s.list}>
          {SUITS.map((x, i) => (
            <div key={i} style={s.suitRow}>
              <p style={s.suitFeel}>"{x.feel}"</p>
              <p style={s.suitGo}>{x.go}</p>
            </div>
          ))}
        </div>
      </section>

      {GROUPS.map((g, gi) => (
        <section key={g.label}>
          <h3 style={s.sectionTitle}>{g.label}</h3>
          <div style={s.list}>
            {g.items.map((f, fi) => {
              const key = `${gi}-${fi}`
              const isOpen = open === key
              return (
                <div key={key} style={s.row}>
                  <button style={s.rowHead} onClick={() => setOpen(isOpen ? null : key)}>
                    <span style={s.rowEmoji}>{f.emoji}</span>
                    <span style={{ flex:1, minWidth:0, textAlign:'left' }}>
                      <span style={s.rowTitle}>{f.name}</span>
                      <span style={s.rowBody}>{f.what}</span>
                    </span>
                    <span style={s.chev}>{isOpen ? '▾' : '▸'}</span>
                  </button>
                  {isOpen && (
                    <div style={s.detail}>
                      <p style={s.detailLabel}>How to use it</p>
                      <p style={s.detailBody}>{f.how}</p>
                      <p style={s.detailLabel}>Reach for it when</p>
                      <p style={s.detailBody}>{f.when}</p>
                      {f.view && (
                        <button style={s.openBtn} onClick={() => onNavigate?.(f.view)}>Open {f.name} →</button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      ))}

      <p style={s.foot}>SOVRANOVA is a wellness and personal-development tool, not therapy or a medical service. If you are in crisis, use the Urgent help button, or contact Lifeline 13 11 14 (AU), 988 (US), or your local emergency number.</p>
    </div>
  )
}

const s = {
  page:         { minHeight:'100vh', background:'var(--og-bg, #f5ead8)', padding:'1.5rem 1.25rem 2.5rem', display:'flex', flexDirection:'column', gap:'1.5rem', color:'var(--og-text, #201e1d)', maxWidth:720, margin:'0 auto', width:'100%', boxSizing:'border-box' },
  back:         { alignSelf:'flex-start', background:'transparent', border:'1.5px solid var(--og-n300, #dcd3c4)', color:'var(--og-n700, #645c50)', fontSize:'0.82rem', fontWeight:600, cursor:'pointer', borderRadius:'999px', padding:'0.35rem 0.9rem', fontFamily:'inherit' },
  hero:         { display:'flex', flexDirection:'column', gap:'0.5rem' },
  kicker:       { fontSize:'0.66rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'var(--og-a700, #8c491a)', fontWeight:700, margin:0 },
  h1:           { fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'1.8rem', fontWeight:400, color:'var(--og-a900, #402310)', lineHeight:1.15, margin:0 },
  lede:         { fontSize:'0.95rem', color:'var(--og-n800, #474238)', lineHeight:1.65, margin:0 },
  sectionTitle: { fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'1.1rem', fontWeight:400, color:'var(--og-a900, #402310)', marginBottom:'0.7rem' },
  list:         { display:'flex', flexDirection:'column', gap:'0.6rem' },
  suitRow:      { background:'var(--og-s100, #f0fae1)', borderRadius:'18px', padding:'0.7rem 0.9rem' },
  suitFeel:     { fontSize:'0.84rem', fontWeight:600, color:'var(--og-s900, #272e1b)', margin:0 },
  suitGo:       { fontSize:'0.78rem', color:'var(--og-s700, #56633f)', lineHeight:1.5, margin:'0.25rem 0 0' },
  row:          { background:'var(--og-n100, #f9f4ed)', borderRadius:'20px', overflow:'hidden' },
  rowHead:      { display:'flex', alignItems:'flex-start', gap:'0.75rem', width:'100%', background:'transparent', border:'none', padding:'0.85rem 0.95rem', cursor:'pointer', fontFamily:'inherit' },
  rowEmoji:     { fontSize:'1.4rem', flexShrink:0, lineHeight:1.2 },
  rowTitle:     { display:'block', fontSize:'0.9rem', fontWeight:700, color:'var(--og-n900, #2e2b25)' },
  rowBody:      { display:'block', fontSize:'0.78rem', color:'var(--og-n700, #645c50)', lineHeight:1.5, marginTop:'0.2rem' },
  chev:         { fontSize:'0.8rem', color:'var(--og-n500, #a19786)', flexShrink:0, paddingTop:'0.15rem' },
  detail:       { padding:'0 0.95rem 0.95rem 3.1rem' },
  detailLabel:  { fontSize:'0.64rem', letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:700, color:'var(--og-a700, #8c491a)', margin:'0.5rem 0 0.15rem' },
  detailBody:   { fontSize:'0.8rem', color:'var(--og-n700, #645c50)', lineHeight:1.6, margin:0 },
  openBtn:      { marginTop:'0.75rem', border:'none', background:'var(--og-accent2, #7a8a5e)', color:'#fff', borderRadius:'999px', padding:'0.5rem 1rem', fontSize:'0.82rem', fontWeight:600, fontFamily:'inherit', cursor:'pointer' },
  foot:         { fontSize:'0.76rem', color:'var(--og-n500, #a19786)', lineHeight:1.6, fontStyle:'italic', borderTop:'1.5px solid var(--og-n200, #eee7db)', paddingTop:'1rem' },
}
